import React, { FormEvent } from "react";
import { ChangeEvent, Fragment } from "react";
import ReactModal from "react-modal";

import OverflowMenuItem from "./OverflowMenuItem";

import { formatDateTimeLocal, formatSize, formatMimeShort } from "../Utilities";

import "../button.css";
import "../list.css";
import { V2 } from "../Api";
import ProgressBar from "./ProgressBar";

interface INote {
    timestamp: string;
    author?: string;
    body: any;
    attachments?: any[];
}

interface INoteBoxProps {
    editorClassName?: string;

    displayName?: string;
    notes?: INote[];

    allowDeletion?: boolean;
    hideControls?: boolean;
    hideAuthor?: boolean;
    defaultNoteOpen?: boolean;
    mode?: "default" | "single";

    allowUpload?: boolean;
    uploadFilter?: string;
    requireUpload?: boolean;
    uploadCompress?: "image";
    hideAttachments?: boolean;
    hideLoading?: boolean;
    stayOpenAfterSubmit?: boolean;

    onNewNote?: (body: string, files?: string[]) => PromiseLike<void>;
    onNewNoteChange?: (body: string) => void;
    onEditorChange?: (isVisible: boolean) => void;
    onNoteDelete?: (index: number) => PromiseLike<void>;
    noteHeaderExtraRender?: (note: any) => React.ReactChild;
    noteAuthorGenerator?: (note: any) => string;
    newNoteHeaderExtraRender?: () => React.ReactChild;
}

interface INoteBoxState {
    isNoteBoxVisible: boolean;
    isSubmittingNote?: string | boolean;
    noteBody: string;

    isDeletionModalOpen: boolean;
    deletionModalLoading: boolean | string;
    pendingIndex?: number;

    pendingAttachments: any[];
    attachmentLoadingNoteIndex?: number;
    attachmentLoadingIndex?: number | string;
    attachmentUploadSuccess: boolean;
}

class NoteBox extends React.Component<INoteBoxProps, INoteBoxState> {
    private attachmentFileRef: React.RefObject<HTMLInputElement>;

    static defaultProps = {
        mode: "default"
    };

    constructor(props: Readonly<INoteBoxProps>) {
        super(props);

        this.state = {
            isNoteBoxVisible: props.defaultNoteOpen === true || props.mode === "single",
            noteBody: "",

            deletionModalLoading: false,
            isDeletionModalOpen: false,

            pendingAttachments: [],
            attachmentUploadSuccess: false,
        };

        this.showNoteEditor = this.showNoteEditor.bind(this);
        this.saveNote = this.saveNote.bind(this);
        this.discardNote = this.discardNote.bind(this);
        this.handleNoteChanged = this.handleNoteChanged.bind(this);

        this.deleteItem = this.deleteItem.bind(this);
        this.realDeleteItem = this.realDeleteItem.bind(this);
        this.closeDeletionModal = this.closeDeletionModal.bind(this);
        this.openAttachment = this.openAttachment.bind(this);
        this.selectAttachment = this.selectAttachment.bind(this);
        this.handleAttachmentsChange = this.handleAttachmentsChange.bind(this);
        this.removeAttachment = this.removeAttachment.bind(this);

        this.attachmentFileRef = React.createRef();
    }

    public render() {
        return (
            <Fragment>
                {
                    this.state.isDeletionModalOpen
                        ? <ReactModal
                            isOpen={this.state.isDeletionModalOpen} onRequestClose={this.closeDeletionModal}
                            overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-note-deletion-portal"
                            bodyOpenClassName="modal-note-deletion-open"
                            contentLabel="Confirm Delete">
                            <form className="input-form" name="note-deletion-form" onSubmit={this.realDeleteItem}>
                                <section className="info-block">
                                    <header>
                                        <h3>Confirm Delete</h3>
                                    </header>

                                    <span>Are you sure you want to delete this note?</span>
                                    &nbsp;
                                    <div className="action-panel-dialog">
                                        <button type="submit" className="button red push-right">Yes, Delete</button>
                                        <button type="button" className="button" onClick={this.closeDeletionModal}>No</button>
                                    </div>
                                </section>
                            </form>
                        </ReactModal>
                        : ""
                }
                <ul className="note-list" id="frm-notes-pre">
                    {this.props.notes
                        ? this.props.notes.map((n: any, i) =>
                            <li key={n.timestamp}>
                                <header>
                                    <div>
                                        {this.props.hideAuthor === true
                                            ? null
                                            : <div>{`${n.author || this.props.noteAuthorGenerator?.(n) || "Author Unknown"}`}</div>
                                        }
                                        <div>{this.props.noteHeaderExtraRender
                                            ? this.props.noteHeaderExtraRender(n)
                                            : formatDateTimeLocal(n.timestamp)
                                        }</div>
                                    </div>
                                    {
                                        this.props.allowDeletion === true
                                            ? <OverflowMenuItem>
                                                <button type="button" onClick={() => this.deleteNote(i)}><i className="icon-trash" /><span>Delete note</span></button>
                                            </OverflowMenuItem>
                                            : undefined
                                    }
                                </header>
                                <p>
                                    {
                                        typeof n.body === "string"
                                            ? n.body.split(/[\n\r]/g).map((s: string, i: number) => (<Fragment key={i}>{s}<br /></Fragment>))
                                            : n.body
                                    }
                                </p>
                                {
                                    this.props.hideAttachments !== true && n.attachments && n.attachments.length > 0
                                        ? <footer>
                                            <small>Attachments:</small>
                                            <div style={{ margin: "0px -2px -2px -2px" }}>
                                                {n.attachments.map((a: any, i2: number) => (
                                                    <button role="link" onClick={() => this.openAttachment(i, i2)} key={a.fileId} className="note-attachment">{a.title || a.fileId} {a.mime && a.contentLength ? <small>({formatMimeShort(a.mime)}, {formatSize(a.contentLength)})</small> : null}
                                                        {(i === this.state.attachmentLoadingNoteIndex && i2 === this.state.attachmentLoadingIndex) ? <span className="note-attachment-loading"><div className="spinner lds-dual-ring-small" />&nbsp;Loading attachment... </span> : null}
                                                    </button>
                                                ))}
                                            </div>
                                        </footer>
                                        : null
                                }
                            </li>
                        )
                        : null
                    }
                    {
                        this.state.isNoteBoxVisible ? (
                            <li>
                                {this.props.mode === "default" || this.props.allowUpload === true
                                    ? <header>
                                        {this.props.mode === "default"
                                            ? <div>
                                                <div>{this.props.displayName || "Author Unknown"}</div>
                                                <div>{formatDateTimeLocal(new Date())}</div>
                                                {this.props.newNoteHeaderExtraRender?.()}
                                            </div>
                                            : <div />}
                                        {
                                            this.props.allowUpload === true && !this.state.attachmentUploadSuccess
                                                ? <Fragment>
                                                    <button type="button" title="Attach files" onClick={this.selectAttachment}><i className="icon-paperclip" /></button>
                                                    <input type="file" ref={this.attachmentFileRef} className="hidden" multiple={true} accept={this.props.uploadFilter} onChange={this.handleAttachmentsChange} />
                                                </Fragment>
                                                : null
                                        }
                                    </header>
                                    : null}
                                <textarea className={this.props.editorClassName} autoFocus={true} value={this.state.noteBody} onChange={this.handleNoteChanged} disabled={this.state.isSubmittingNote === true} placeholder="Enter note here..." />
                                {
                                    this.state.pendingAttachments.length > 0
                                        ? <footer>
                                            <small>Attachments:</small>
                                            <div style={{ margin: "0px -2px -2px -2px" }}>
                                                {this.state.pendingAttachments.map((a: any, i: number) => (
                                                    <div key={i} className="note-attachment-pending">
                                                        <div>
                                                            <span>{a.title || a.fileId || "No Description"} <small>({formatMimeShort(a.mime)}, {formatSize(a.contentLength)})</small></span>
                                                            {this.state.isSubmittingNote !== true && !this.state.attachmentUploadSuccess ? <button type="button" title="Remove attachment" onClick={() => this.removeAttachment(i)}><i className="icon-times" /></button> : null}
                                                        </div>
                                                        {(typeof a.progress !== "undefined") ? <ProgressBar value={a.progress || 0} /> : null}
                                                    </div>
                                                ))}
                                            </div>
                                        </footer>
                                        : null
                                }
                            </li>
                        ) : ""
                    }
                </ul>
                <div className="action-panel-dialog">
                    {
                        typeof this.state.isSubmittingNote === "string" ?
                            <span className="error-text">{this.state.isSubmittingNote}</span> :
                            this.state.isSubmittingNote === true && !(this.props.hideLoading === true) ?
                                <div className="spinner spinner-inline lds-dual-ring" /> :
                                ""
                    }
                    {
                        !this.props.hideControls && this.props.mode === "default"
                            ? this.state.isNoteBoxVisible
                                ? (<Fragment>
                                    <button type="button" className="button push-right" onClick={this.saveNote} disabled={this.state.isSubmittingNote === true}>Save</button>
                                    <button type="button" className="button" onClick={this.discardNote} disabled={this.state.isSubmittingNote === true}>Discard</button>
                                </Fragment>)
                                : (<button type="button" className="button btn-light btn-plus push-right" onClick={this.showNoteEditor}>Add Note</button>)
                            : ""
                    }
                </div>
            </Fragment>
        );
    }

    public async submit() {
        return this.saveNote();
    }

    private showNoteEditor() {
        this.setState({
            isNoteBoxVisible: true
        }, () => {
            if (this.props.onEditorChange) {
                this.props.onEditorChange(true);
            }
        });
    }

    private async saveNote() {
        if (this.props.onNewNote) {
            this.setState({
                isSubmittingNote: true
            });

            try {
                // Upload files
                if (!this.state.attachmentUploadSuccess) {
                    await this.uploadPendingAttachments();
                }

                await this.props.onNewNote(this.state.noteBody, this.state.pendingAttachments.map(a => a.fileId));
                if (this.props.stayOpenAfterSubmit === true) {
                    this.setState({
                        isNoteBoxVisible: true,
                        isSubmittingNote: false,
                    });
                }
                else {
                    this.setState({
                        isNoteBoxVisible: false,
                        isSubmittingNote: false,

                        noteBody: "",
                        pendingAttachments: [],
                        attachmentUploadSuccess: false,
                    }, () => {
                        if (this.props.onEditorChange) {
                            this.props.onEditorChange(false);
                        }
                    });
                }

                return true;
            }
            catch (e) {
                this.setState({
                    isSubmittingNote: (e && (e.message || e.toString())) || "Unknown error"
                });
                return false;
            }
        }

        return false;
    }

    private discardNote() {
        this.setState({
            isNoteBoxVisible: false
        }, () => {
            if (this.props.onEditorChange) {
                this.props.onEditorChange(false);
            }
        });
    }

    private handleNoteChanged(e: ChangeEvent<HTMLTextAreaElement>) {
        this.setState({
            noteBody: e.currentTarget.value
        }, () => {
            if (this.props.onNewNoteChange) {
                this.props.onNewNoteChange(this.state.noteBody);
            }
        });
    }

    private async deleteNote(index: number) {
        if (!this.props.onNoteDelete) {
            return;
        }

        this.setState({
            isDeletionModalOpen: true,
            pendingIndex: index,
        });
    }

    private deleteItem() {
        this.setState({
            isDeletionModalOpen: true
        });
    }

    private closeDeletionModal() {
        this.setState({
            isDeletionModalOpen: false
        });
    }

    private async realDeleteItem(e?: FormEvent) {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        console.log("Deletion dialog form submit");

        if (!this.props.onNoteDelete || this.state.pendingIndex === undefined) {
            return;
        }

        this.setState({
            isDeletionModalOpen: false,
            isSubmittingNote: true,
        });

        try {
            await this.props.onNoteDelete(this.state.pendingIndex);
            this.setState({
                isSubmittingNote: false,

            });
        }
        catch (e) {
            this.setState({
                isSubmittingNote: (e && (e.message || e.toString())) || "Unknown error"
            });
        }
    }

    private async openAttachment(noteIndex: number, attachmentIndex: number) {
        try {
            if (!this.props.notes || !this.props.notes[noteIndex] || !this.props.notes[noteIndex].attachments) {
                return;
            }

            this.setState({
                attachmentLoadingNoteIndex: noteIndex,
                attachmentLoadingIndex: attachmentIndex
            });

            const attachment = this.props.notes[noteIndex].attachments![attachmentIndex];

            // Load attachment into blob
            const v = await V2.get<Blob>(`files/${attachment.fileId}`);

            const blob = URL.createObjectURL(v);
            const wnd = window.open(blob, "file_window", "resizable=on,scrollbars=on,status=on,location=on");

            this.setState({
                attachmentLoadingNoteIndex: undefined,
                attachmentLoadingIndex: undefined
            });

            if (wnd) {
                wnd.addEventListener("unload", e => {
                    if ((e.currentTarget! as Window).location.protocol === "blob:") {
                        URL.revokeObjectURL(blob);
                    }
                });
            }
        }
        catch (e) {
            this.setState({
                attachmentLoadingIndex: e
            });
        }
    }

    private selectAttachment() {
        if (!this.attachmentFileRef.current) {
            return;
        }

        this.attachmentFileRef.current.click();
    }

    private handleAttachmentsChange(e: ChangeEvent<HTMLInputElement>) {
        const pendingAttachments = this.state.pendingAttachments;
        const fileList = e.currentTarget.files;
        if (!fileList) {
            return;
        }

        for (let i = 0; i < fileList.length; i++) {
            const file = fileList[i];
            pendingAttachments.push({
                contentLength: file.size,
                mime: file.type || "application/octet-stream",
                title: file.name,
                file
            });
        }

        this.setState({
            pendingAttachments
        });
    }

    private removeAttachment(index: number) {
        const pendingAttachments = this.state.pendingAttachments;

        pendingAttachments.splice(index, 1);

        this.setState({
            pendingAttachments
        });
    }

    private async uploadPendingAttachments() {
        if (this.state.pendingAttachments.length === 0 && this.props.requireUpload === true) {
            throw new Error("A file must be attached");
        }

        for (const item of this.state.pendingAttachments) {
            const fd = new FormData();
            fd.append("", item.file);
            fd.append("description", item.title);

            const fileInfo = await V2.post<any>(this.props.uploadCompress ? `files?compress=${this.props.uploadCompress}` : `files`, fd, (current, total) => {
                item.progress = (current * 100 / total);

                this.setState(s => ({
                    pendingAttachments: s.pendingAttachments
                }));
            });

            item.fileId = fileInfo.id;
        }

        this.setState({
            attachmentUploadSuccess: true
        });
    }
}

export default NoteBox;
